import { memo, useCallback, useId, type ReactNode } from 'react'

interface ModalShellProps {
  children: ReactNode
  className?: string
  closeDisabled?: boolean
  dismissOnOverlay?: boolean
  onClose: () => void
  showCloseButton?: boolean
  title: string
  visible?: boolean
  size?: 'narrow' | 'medium' | 'wide'
}

export const ModalShell = memo(function ModalShell({
  children,
  className = '',
  closeDisabled = false,
  dismissOnOverlay = true,
  onClose,
  showCloseButton = true,
  title,
  visible = true,
  size = 'medium',
}: ModalShellProps) {
  const titleId = useId()

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || closeDisabled) return
    event.preventDefault()
    onClose()
  }, [closeDisabled, onClose])

  const handleOverlayClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (dismissOnOverlay && !closeDisabled && event.target === event.currentTarget) onClose()
  }, [closeDisabled, dismissOnOverlay, onClose])

  return (
    <div
      className={`tp-modal-overlay${visible ? ' is-visible' : ''}`}
      onKeyDown={handleKeyDown}
      onMouseDown={handleOverlayClick}
    >
      <section
        className={`tp-modal tp-modal--${size}${visible ? ' is-visible' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="tp-modal__header">
          <h2 id={titleId} className="tp-modal__title">{title}</h2>
          {showCloseButton && (
            <button type="button" className="tp-icon-button tp-modal__close" onClick={onClose} aria-label="Fermer" disabled={closeDisabled}>×</button>
          )}
        </header>
        {children}
      </section>
    </div>
  )
})
